import React from 'react'

import { ZariContext } from '_zari/context'

import { Form, Col, Modal, Button, Row, Divider } from '_zari/core'

import { DateHandler, RounderUtil } from 'utils'

import * as FormGroups from './formGroups'
import * as Sections from './sections'

const FormularioPagosModal = ({ handleClose, show, submitData, mainFormData, submitFunction }) => {
  const __zariContext = React.useContext(ZariContext)

  const [montoTotal, setMontoTotal] = React.useState(submitData.importeTotal)
  const [cantidadCuotas, setCantidadCuotas] = React.useState(
    submitData.cantidadCuotas ? `${submitData.cantidadCuotas}` : '1'
  )
  const [sustraccionesAplicadas, setSustraccionesAplicadas] = React.useState([])
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [mostrarTabla, setMostrarTabla] = React.useState(!!submitData.cuotas)

  const cuotasRef = React.useRef(submitData.cuotas || [])
  const infoUnicaCuotaRef = React.useRef(null)

  const recalcularMontoTotal = React.useCallback(
    inputSustracciones => {
      let innerMontoTotal = 0
      if (inputSustracciones && inputSustracciones.length > 0) {
        for (let i = 0; i < inputSustracciones.length; i++) {
          const element = inputSustracciones[i]

          innerMontoTotal = RounderUtil.round(innerMontoTotal + element.monto || 0)
        }
      }

      setMontoTotal(RounderUtil.round(submitData.importeTotal - innerMontoTotal))
    },
    [submitData.importeTotal]
  )

  const clickContinuar = React.useCallback(() => {
    if (!cantidadCuotas) {
      __zariContext.snack('error', 'Ingresar cantidad de cuotas.')
      return
    }

    if (parseFloat(cantidadCuotas) === 1 && !submitData.cuotas) {
      const fechaEmisionDate = DateHandler.parse(submitData.fechaEmision)
      const fechaCuota = DateHandler.add(fechaEmisionDate, { days: 30 })
      const fechaCuotaStr = DateHandler.format(fechaCuota)

      const nuevoObjeto = {
        orden: 1,
        plazoDias: 30,
        fecha: fechaCuotaStr,
        monto: montoTotal
      }

      infoUnicaCuotaRef.current = nuevoObjeto
      cuotasRef.current = [nuevoObjeto]
    }

    setMostrarTabla(true)
  }, [cantidadCuotas, __zariContext, submitData, montoTotal])

  const clickGuardar = React.useCallback(() => {
    setIsSubmitting(true)

    const sustracciones = sustraccionesAplicadas
    const sustraccionesNormalizadas = []
    for (let i = 0; i < sustracciones.length; i++) {
      const sus = sustracciones[i]
      if (!sus.monto || sus.monto === 0) {
        __zariContext.snack('error', 'No se permite sustracciones con monto cero.')
        return
      }

      if (!sus._idSustraccionVenta) {
        __zariContext.snack('error', 'Seleccionar tipo de sustraccion.')
        return
      }

      sustraccionesNormalizadas.push({
        _idSustraccionVenta: sus._idSustraccionVenta,
        monto: parseFloat(sus.monto)
      })
    }

    if (!cantidadCuotas) {
      __zariContext.snack('error', 'Ingresar cantidad de cuotas.')
      return
    }

    const intCantidadCuotas = parseInt(cantidadCuotas)

    const cuotas = cuotasRef.current

    const cantidadCuotasArray = cuotas.length

    if (intCantidadCuotas === 0 || cantidadCuotasArray === 0) {
      __zariContext.snack('error', 'Ingresar cuotas.')
      setIsSubmitting(false)
      return
    }

    if (intCantidadCuotas !== cantidadCuotasArray) {
      __zariContext.snack('error', 'La cantidad de cuotas ingresadas no coincide con la cantidad indicada.')
      setIsSubmitting(false)
      return
    }

    const cuotasNormalizadas = cuotas.map(x => {
      return {
        orden: x.orden,
        plazoDias: parseFloat(x.plazoDias),
        fecha: x.fecha,
        monto: parseFloat(x.monto)
      }
    })

    let sumaPagos = 0

    for (let i = 0; i < cuotasNormalizadas.length; i++) {
      const innerCuota = cuotasNormalizadas[i]

      sumaPagos = RounderUtil.round(sumaPagos + innerCuota.monto)
    }

    if (sumaPagos !== montoTotal) {
      __zariContext.snack('error', 'La suma de cuotas no coincide con el monto total.')
      setIsSubmitting(false)
      return
    }

    mainFormData.cantidadCuotas = intCantidadCuotas
    mainFormData.cuotas = cuotasNormalizadas
    mainFormData.sustracciones = sustraccionesNormalizadas

    submitFunction(
      {
        ...submitData,
        cantidadCuotas: intCantidadCuotas,
        cuotas: cuotasNormalizadas,
        sustracciones: sustraccionesNormalizadas
      },
      { setSubmitting: setIsSubmitting }
    )
  }, [__zariContext, submitData, mainFormData, submitFunction, cantidadCuotas, montoTotal, sustraccionesAplicadas])

  return (
    <Modal show={show}>
      <Modal.Header>
        <Modal.Title>GENERADO DE CUOTAS</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Divider title='Sustracciones' />
        <Row>
          <Col md={12}>
            <Sections.SustraccionesSection
              montoBase={submitData.importeTotal}
              sustraccionesAplicadas={sustraccionesAplicadas}
              setSustraccionesAplicadas={setSustraccionesAplicadas}
              recalcularMontoTotal={recalcularMontoTotal}
            />
          </Col>
        </Row>
        <Divider title='Cuotas' />
        <Row>
          <Col md={4}>
            <FormGroups.ImporteTotalFormGroup value={montoTotal} />
          </Col>
          <Col md={4}>
            <FormGroups.CantidadCuotasFormGroup
              onChange={e => setCantidadCuotas(e.target.value)}
              value={cantidadCuotas}
              fIsSubmitting={isSubmitting}
            />
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label text='.' md={12} alignRightAt='md' />
              <Col md={12}>
                <Button onClick={clickContinuar} tiny outline>
                  Continuar
                </Button>
              </Col>
            </Form.Group>
          </Col>
        </Row>
        {mostrarTabla && (
          <Sections.TablaSection
            cuotasRef={cuotasRef}
            cantidadCuotas={cantidadCuotas ? parseFloat(cantidadCuotas) : 0}
            edicionCuotasData={submitData.cuotas || []}
            infoUnicaCuota={infoUnicaCuotaRef.current}
            fechaEmisionDocumento={submitData.fechaEmision}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button color='secondary' onClick={handleClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button id='boton-guardar-cuotas' onClick={clickGuardar} disabled={isSubmitting}>
          Guardar
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default React.memo(FormularioPagosModal)
